import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MapPin, X, MapTrifold, QrCode, Printer, UploadSimple, FileCsv } from "@phosphor-icons/react";
import MapPicker from "@/components/MapPicker";

// Minimal CSV parser: handles quoted values, commas inside quotes, escaped quotes
const parseCsv = (text) => {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { header: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuote) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQuote = false; }
        else cur += c;
      } else if (c === '"') { inQuote = true; }
      else if (c === ",") { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z0-9_]/g, "_"));
  const rows = lines.slice(1).map(parseLine);
  return { header, rows };
};

const headerAliases = {
  name: ["name", "site_name", "site", "location", "location_name"],
  address: ["address", "street", "street_address", "location_address"],
  code: ["code", "site_code", "post", "post_code"],
  notes: ["notes", "note", "comment", "comments", "instructions"],
  latitude: ["latitude", "lat"],
  longitude: ["longitude", "lng", "lon", "long"],
  geofence_radius_m: ["geofence_radius_m", "radius", "geofence", "geofence_m"],
  patrol_interval_minutes: ["patrol_interval_minutes", "patrol", "patrol_minutes", "patrol_interval"],
};

const mapRowToSite = (header, row) => {
  const obj = {};
  header.forEach((h, i) => { obj[h] = row[i] ?? ""; });
  const pick = (key) => {
    for (const a of headerAliases[key]) if (obj[a]) return obj[a];
    return "";
  };
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };
  const int = (v) => { const n = parseInt(v); return Number.isFinite(n) ? n : undefined; };
  return {
    name: pick("name"),
    address: pick("address"),
    code: pick("code") || undefined,
    notes: pick("notes") || undefined,
    latitude: num(pick("latitude")),
    longitude: num(pick("longitude")),
    geofence_radius_m: int(pick("geofence_radius_m")),
    patrol_interval_minutes: int(pick("patrol_interval_minutes")),
  };
};

const EMPTY_SITE = {
  name: "", address: "", code: "", notes: "",
  latitude: null, longitude: null, geofence_radius_m: 100,
};

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SITE);
  const [tab, setTab] = useState("details"); // details | geofence | checkpoints

  // Checkpoints state for the currently-editing site
  const [checkpoints, setCheckpoints] = useState([]);
  const [newCp, setNewCp] = useState("");

  // CSV import
  const fileInputRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sites");
    setSites(data);
  };
  useEffect(() => { const id = setTimeout(load, 0); return () => clearTimeout(id); }, []);

  const loadCheckpoints = useCallback(async (siteId) => {
    if (!siteId) { setCheckpoints([]); return; }
    try {
      const { data } = await api.get(`/sites/${siteId}/checkpoints`);
      setCheckpoints(data);
    } catch { setCheckpoints([]); }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_SITE);
    setTab("details");
    setCheckpoints([]);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, address: s.address, code: s.code || "", notes: s.notes || "",
      latitude: s.latitude ?? null, longitude: s.longitude ?? null,
      geofence_radius_m: s.geofence_radius_m ?? 100,
    });
    setTab("details");
    setOpen(true);
    loadCheckpoints(s.site_id);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    try {
      if (editing) await api.patch(`/sites/${editing.site_id}`, form);
      else {
        const { data } = await api.post("/sites", form);
        setEditing(data); // so the user can add checkpoints next
      }
      toast.success(editing ? "Site updated" : "Site added");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete site ${s.name}?`)) return;
    await api.delete(`/sites/${s.site_id}`);
    toast.success("Site removed");
    await load();
  };

  const addCheckpoint = async () => {
    const name = newCp.trim();
    if (!name || !editing) return;
    try {
      await api.post("/checkpoints", { site_id: editing.site_id, name, order: checkpoints.length });
      setNewCp("");
      await loadCheckpoints(editing.site_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeCp = async (cp) => {
    if (!window.confirm(`Delete checkpoint "${cp.name}"?`)) return;
    await api.delete(`/checkpoints/${cp.checkpoint_id}`);
    await loadCheckpoints(editing.site_id);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { header, rows } = parseCsv(String(reader.result || ""));
        if (header.length === 0) { toast.error("Empty CSV"); return; }
        const mapped = rows.map((r) => mapRowToSite(header, r));
        const valid = mapped.filter((m) => m.name && m.address);
        if (valid.length === 0) {
          toast.error("No valid rows. Make sure CSV has 'name' and 'address' columns.");
          return;
        }
        setImportRows(valid);
        setImportOpen(true);
      } catch {
        toast.error("Failed to parse CSV");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/sites/import", { rows: importRows });
      toast.success(`Imported ${data.imported} sites (${data.skipped} duplicates skipped${data.errors.length ? `, ${data.errors.length} errors` : ""})`);
      setImportOpen(false);
      setImportRows([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sites-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">// LOCATIONS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Sites & Posts</h1>
          <p className="text-sm text-zinc-500 mt-1">Locations, geofences, and patrol checkpoints.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="sites-csv-input"
            className="hidden"
            onChange={handleFile}
          />
          <button
            data-testid="import-sites-csv-btn"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 border border-white/15 hover:bg-white/5 px-4 py-2.5 text-xs uppercase tracking-wider font-semibold"
          >
            <UploadSimple size={14} weight="bold" /> Import CSV
          </button>
          <button data-testid="add-site-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#007AFF] hover:bg-[#0066d6] px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">
            <Plus size={14} weight="bold" /> Add Site
          </button>
        </div>
      </div>

      {sites.length === 0 ? (
        <div className="tac-card p-12 text-center">
          <div className="label-mono mb-2">// NO SITES YET</div>
          <p className="text-sm text-zinc-400">Add your first site to dispatch guards to.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((s) => (
            <div key={s.site_id} className="tac-card p-5 group" data-testid={`site-card-${s.site_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 grid place-items-center bg-white/5 border border-white/10 text-[#2BD4D9]">
                  <MapPin size={18} weight="duotone" />
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Link to={`/sites/${s.site_id}/print`} title="Print checkpoint QR codes" className="border border-white/15 hover:bg-white/5 p-1.5">
                    <Printer size={12}/>
                  </Link>
                  <button data-testid={`edit-site-${s.site_id}`} onClick={() => openEdit(s)} className="border border-white/15 hover:bg-white/5 p-1.5"><PencilSimple size={12}/></button>
                  <button data-testid={`delete-site-${s.site_id}`} onClick={() => remove(s)} className="border border-white/15 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 p-1.5"><Trash size={12}/></button>
                </div>
              </div>
              <div className="font-semibold text-lg leading-tight">{s.name}</div>
              {s.code && <div className="label-mono mt-1">CODE · {s.code}</div>}
              <div className="text-sm text-zinc-400 mt-2">{s.address}</div>
              <div className="mt-3 flex items-center gap-3 text-[0.65rem] font-mono uppercase flex-wrap">
                {typeof s.latitude === "number" ? (
                  <span className="text-emerald-400 flex items-center gap-1"><MapTrifold size={10}/> Geofence · {s.geofence_radius_m}m</span>
                ) : (
                  <span className="text-zinc-600">No geofence set</span>
                )}
                {(s.patrol_interval_minutes ?? 0) > 0 && (
                  <span className="text-[#2BD4D9] flex items-center gap-1">Patrol · {s.patrol_interval_minutes}m</span>
                )}
              </div>
              {s.notes && <div className="mt-3 text-xs text-zinc-500 border-t border-white/5 pt-3">{s.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="site-modal">
          <div className="bg-[#121212] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3 sticky top-0 bg-[#121212] z-10">
              <div className="label-mono">{editing ? "// EDIT SITE" : "// ADD SITE"}</div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10 px-5">
              {["details", "geofence", "checkpoints"].map((t) => (
                <button
                  key={t}
                  data-testid={`tab-${t}`}
                  onClick={() => setTab(t)}
                  disabled={t === "checkpoints" && !editing}
                  className={`px-4 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${
                    tab === t ? "border-[#007AFF] text-white" : "border-transparent text-zinc-500 hover:text-white"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {t === "details" ? "Details" : t === "geofence" ? "Geofence" : "Checkpoints"}
                </button>
              ))}
            </div>

            {tab === "details" && (
              <form onSubmit={save} className="p-5 space-y-3">
                <div>
                  <label className="label-mono block mb-1">Site Name</label>
                  <input data-testid="site-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">Address</label>
                  <input data-testid="site-address" required value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">Site Code</label>
                  <input data-testid="site-code" value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} placeholder="POST-A1" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">Notes</label>
                  <textarea data-testid="site-notes" value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} rows={2} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Close</button>
                  <button data-testid="save-site-btn" type="submit" className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">
                    {editing ? "Save details" : "Create site"}
                  </button>
                </div>
              </form>
            )}

            {tab === "geofence" && (
              <div className="p-5 space-y-4">
                <div className="text-xs text-zinc-400">
                  Drop a pin on the site and set the geofence radius. When a guard taps their check-in link, Sandy compares their GPS to this fence.
                </div>
                <MapPicker
                  lat={form.latitude}
                  lng={form.longitude}
                  radius={form.geofence_radius_m}
                  addressHint={form.address}
                  onChange={({ lat, lng, radius }) => setForm((f) => ({ ...f, latitude: lat, longitude: lng, geofence_radius_m: radius }))}
                />
                <div>
                  <label className="label-mono block mb-1">Radius: {form.geofence_radius_m}m</label>
                  <input
                    data-testid="radius-slider"
                    type="range" min={25} max={500} step={5}
                    value={form.geofence_radius_m}
                    onChange={(e) => setForm({ ...form, geofence_radius_m: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-[0.65rem] font-mono text-zinc-600">
                    <span>25m</span><span>250m</span><span>500m</span>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <label className="label-mono block mb-2">// Patrol Schedule</label>
                  <p className="text-xs text-zinc-500 mb-2">
                    Require a checkpoint scan every N minutes during a shift. 0 = disabled. SMS alert sent if interval is exceeded.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="patrol-interval"
                      type="number"
                      min={0} max={240} step={5}
                      value={form.patrol_interval_minutes ?? 0}
                      onChange={(e) => setForm({ ...form, patrol_interval_minutes: parseInt(e.target.value || "0") })}
                      className="w-24 bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"
                    />
                    <span className="text-xs text-zinc-400">minutes between scans</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {[0, 30, 60, 90, 120].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setForm({ ...form, patrol_interval_minutes: m })}
                        className={`px-2 py-1 text-[10px] font-mono uppercase border ${(form.patrol_interval_minutes ?? 0) === m ? "border-[#007AFF] text-white" : "border-white/15 text-zinc-500 hover:border-white/30"}`}
                      >
                        {m === 0 ? "Off" : `${m}m`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Close</button>
                  <button data-testid="save-geofence-btn" onClick={save} className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">
                    Save geofence
                  </button>
                </div>
              </div>
            )}

            {tab === "checkpoints" && editing && (
              <div className="p-5 space-y-4">
                <div className="text-xs text-zinc-400">
                  Add checkpoints for guard tour (Front Gate, Pool, Back Door, etc). Print QR codes and post them around the site. Guards scan to log presence.
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="checkpoint-name"
                    value={newCp}
                    onChange={(e) => setNewCp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCheckpoint(); } }}
                    placeholder="e.g. Front Gate"
                    className="flex-1 bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"
                  />
                  <button data-testid="add-checkpoint-btn" onClick={addCheckpoint} className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">
                    <Plus size={12} className="inline -mt-0.5 mr-1"/>Add
                  </button>
                </div>
                <div className="space-y-1">
                  {checkpoints.length === 0 ? (
                    <div className="text-xs text-zinc-600 py-4 text-center border border-dashed border-white/10">No checkpoints yet</div>
                  ) : checkpoints.map((cp, i) => (
                    <div key={cp.checkpoint_id} className="flex items-center gap-3 border border-white/10 px-3 py-2" data-testid={`checkpoint-row-${cp.checkpoint_id}`}>
                      <QrCode size={16} className="text-[#2BD4D9]"/>
                      <span className="text-xs text-zinc-500 font-mono">#{i + 1}</span>
                      <span className="flex-1 text-sm">{cp.name}</span>
                      <button onClick={() => removeCp(cp)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-checkpoint-${cp.checkpoint_id}`}>
                        <Trash size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-white/10">
                  <Link
                    to={`/sites/${editing.site_id}/print`}
                    target="_blank"
                    className="text-xs uppercase tracking-wider font-semibold text-[#2BD4D9] hover:text-white flex items-center gap-1"
                  >
                    <Printer size={12}/> Open print sheet
                  </Link>
                  <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="sites-import-modal">
          <div className="bg-[#121212] border border-white/10 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="flex items-center gap-2">
                <FileCsv size={16} className="text-[#2BD4D9]"/>
                <div className="label-mono">// IMPORT PREVIEW · {importRows.length} ROWS</div>
              </div>
              <button onClick={() => setImportOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <div className="overflow-auto p-5 flex-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-white/10">
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">#</th>
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">Name</th>
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">Address</th>
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">Code</th>
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">Geofence</th>
                    <th className="py-2 pr-3 font-mono uppercase tracking-wider">Patrol</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 pr-3 text-zinc-500 font-mono">{i + 1}</td>
                      <td className="py-2 pr-3">{r.name}</td>
                      <td className="py-2 pr-3 text-zinc-400">{r.address}</td>
                      <td className="py-2 pr-3 font-mono text-zinc-400">{r.code || "—"}</td>
                      <td className="py-2 pr-3 font-mono text-zinc-400">
                        {typeof r.latitude === "number" && typeof r.longitude === "number"
                          ? `${r.latitude.toFixed(4)},${r.longitude.toFixed(4)} · ${r.geofence_radius_m ?? 100}m`
                          : "—"}
                      </td>
                      <td className="py-2 pr-3 font-mono text-zinc-400">{r.patrol_interval_minutes ? `${r.patrol_interval_minutes}m` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-[0.65rem] text-zinc-500 font-mono">
                Duplicates (matched by code, or by name+address) will be skipped automatically.
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-3">
              <button type="button" onClick={() => setImportOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
              <button
                data-testid="confirm-sites-import-btn"
                onClick={confirmImport}
                disabled={importing}
                className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                {importing ? "Importing..." : `Import ${importRows.length} sites`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitesPage;
